import React, { FC, useState } from 'react';

import { useObservability } from 'shared/helpers/observability';
import { Box, Flex, Text, Button, DropdownSingleSelect } from '@opendoor/bricks/core';
import { showToast } from '@opendoor/bricks/complex/Toast';
import { DatePicker } from '@opendoor/bricks/complex/DatePicker';

import { LateCheckoutCosts } from 'sellDirect/components/closingDash/lateCheckout/LateCheckoutCosts';

import { ClosingTasks } from 'sellDirect/helpers/closingTasks';
import { OCCUPANCY_STATUS_AT_ACQUISITION_OPTIONS } from 'sellDirect/helpers/closingDash/disclosures';
import { LCO_MAX_DAYS_AFTER_COE } from 'sellDirect/helpers/closingDash/constants';
import { UnderContractOffer } from 'sellDirect/OfferContext';
import { useRequestLateCheckout } from 'sellDirect/graphql/useRequestLateCheckout';
import {
  IResetableFormProps,
  IResetableFormRenderProps,
  ResetableForm,
} from 'sellDirect/components/closingDash/ResetableForm';
import { useCancelLateCheckout } from 'sellDirect/graphql/useCancelLateCheckout';
import {
  useUpdateClosingTasks,
  ClosingTaskSubmissionEventName,
} from 'sellDirect/graphql/useUpdateClosingTasks';
import { convert, LocalDate } from '@js-joda/core';
import { FULL_DATE_AND_DAY_OF_WEEK_FORMAT, localDateFromJsDate } from 'shared/helpers/datetime';

interface LateCheckoutFormState {
  /**
   * This must be the in the form of `LocalDate.toString` ("2016-12-24" ISO 8601 format).
   *
   * We cannot use `LocalDate` because `ResetableForm` uses strict equality to compare values,
   * which does not work on `LocalDate`.
   */
  lateCheckoutDate: string | undefined;
  occupancy: string | undefined;
}

interface LateCheckoutFormProps {
  /** Offer that is under contract */
  offer: UnderContractOffer;
  /** The late checkout task */
  lateCheckoutTask: ClosingTasks['lateCheckout'];
  /** The current occupancy of the home */
  occupancy: string | undefined;
  /** Callback to call when the user requested a late checkout */
  onLateCheckoutRequested: () => void;
  /** Callback to call when the user canceled a late checkout */
  onLateCheckoutCanceled: () => void;
  /** Callback to call when the user canceled the form */
  onCanceled: () => void;
}

/**
 * Renders the form to request a late checkout
 *
 * component-test-check-ignore storybook-check-ignore
 */
export const LateCheckoutForm: FC<LateCheckoutFormProps> = ({
  offer,
  lateCheckoutTask,
  occupancy,
  onLateCheckoutRequested,
  onLateCheckoutCanceled,
  onCanceled,
}) => {
  const { trackEvent } = useObservability();

  const lateCheckoutTaskable = lateCheckoutTask?.taskable;
  const canCancelLateCheckout = lateCheckoutTask?.complete;

  const [formState, setFormState] = useState<LateCheckoutFormState>({
    lateCheckoutDate: lateCheckoutTaskable?.lateCheckoutDate?.toString(),
    occupancy,
  });

  const { updateClosingTasks, loading: updatingClosingTasks } = useUpdateClosingTasks(
    'update-late-checkout-occupancy',
    {
      onCompleted: onLateCheckoutRequested,
      errorPolicy: 'none',
      onError: () =>
        showToast(
          'error',
          'Oops, something went wrong. Please try again in a few hours or contact your experience partner.',
          'cake-update-late-checkout-occupancy-error-toast',
          { toastOptions: { position: 'bottom-left' } },
        ),
    },
  );

  const { requestLateCheckout, loading: requestingLateCheckout } = useRequestLateCheckout({
    onCompleted: () => trackEvent(ClosingTaskSubmissionEventName, 'update-late-checkout-date'),
    errorPolicy: 'none',
    onError: () =>
      showToast(
        'error',
        'Oops, something went wrong. Please try again in a few hours or contact your experience partner.',
        'cake-update-late-checkout-date-error-toast',
        { toastOptions: { position: 'bottom-left' } },
      ),
  });

  const { cancelLateCheckout, loading: cancelingLateCheckout } = useCancelLateCheckout({
    onCompleted: () => {
      trackEvent(ClosingTaskSubmissionEventName, 'cancel-late-checkout-date');
      onLateCheckoutCanceled();
    },
    errorPolicy: 'none',
    onError: () =>
      showToast(
        'error',
        'Oops, something went wrong. Please try again in a few hours or contact your experience partner.',
        'cake-cancel-late-checkout-date-error-toast',
        { toastOptions: { position: 'bottom-left' } },
      ),
  });

  const onSubmit: IResetableFormProps<LateCheckoutFormState>['onSubmit'] = async (
    resetableForm,
  ) => {
    setFormState(resetableForm.formState);
    const occupancy = resetableForm.formState.occupancy;
    const selectedDate = resetableForm.formState.lateCheckoutDate
      ? LocalDate.parse(resetableForm.formState.lateCheckoutDate)
      : undefined; // YYYY-MM-DD format already
    if (selectedDate && occupancy) {
      trackEvent('click', 'request-late-checkout');
      await requestLateCheckout(selectedDate);
      updateClosingTasks({
        otherHomeInfo: {
          occupancyStatusAtAcquisition: occupancy,
        },
      });
    }
  };

  const renderForm = (resetableForm: IResetableFormRenderProps<LateCheckoutFormState>) => (
    <LateCheckoutFormContent
      offer={offer}
      lateCheckoutTask={lateCheckoutTask}
      resetableForm={resetableForm}
      requestingLateCheckout={requestingLateCheckout || updatingClosingTasks}
      canceling={cancelingLateCheckout}
      cancelLateCheckout={canCancelLateCheckout ? cancelLateCheckout : undefined}
      onCanceled={onCanceled}
    />
  );

  return <ResetableForm initialFormState={formState} onSubmit={onSubmit} render={renderForm} />;
};

interface ILateCheckoutFormContent {
  /** Optional callback to call to cancel current late checkout */
  cancelLateCheckout: undefined | (() => void);
  /** True while canceling the current late checkout */
  canceling: boolean;
  /** The late checkout task */
  lateCheckoutTask: ClosingTasks['lateCheckout'];
  /** Should be set to true while requesting a late checkout */
  requestingLateCheckout: boolean;
  /** Current offer */
  offer: UnderContractOffer;
  /** State of the form */
  resetableForm: IResetableFormRenderProps<LateCheckoutFormState>;
  /** Callback to call when the user canceled the form */
  onCanceled: () => void;
}

/** Renders the content of the Late Checkout form */
const LateCheckoutFormContent: React.FC<ILateCheckoutFormContent> = ({
  cancelLateCheckout,
  canceling,
  lateCheckoutTask,
  requestingLateCheckout,
  offer,
  resetableForm,
  onCanceled,
}) => {
  const canCancelLateCheckout = lateCheckoutTask?.complete;

  const earliestDayForLateCheckout = offer.closeEscrowDate.plusDays(1);
  const lastDayForLateCheckout = offer.closeEscrowDate.plusDays(LCO_MAX_DAYS_AFTER_COE);

  const { formState, onSubmit, onUpdate, isEdited } = resetableForm;
  const { occupancy } = formState;
  const lateCheckoutDate = formState.lateCheckoutDate
    ? LocalDate.parse(formState.lateCheckoutDate)
    : undefined;

  return (
    <>
      <Box data-testid="late-checkout-occupancy-question">
        <DropdownSingleSelect
          aria-labelledby="home-occupancy-status"
          autoComplete="off"
          title="What's the occupancy of the home?"
          placeholder="Select an option"
          selectedValue={occupancy ? [occupancy] : undefined}
          onChange={(value) => {
            if (typeof value === 'string') {
              onUpdate('occupancy', value);
            }
          }}
          isDisabled={requestingLateCheckout || canceling}
          options={OCCUPANCY_STATUS_AT_ACQUISITION_OPTIONS}
        />
      </Box>

      {/* Date */}
      <Box mt="7">
        <DatePicker
          id="change-close-of-escrow-date-picker"
          label="Request late checkout (Optional)"
          placeholder="Choose a date"
          data-testid="date-picker"
          disabled={requestingLateCheckout || canceling}
          initialDate={lateCheckoutDate ? convert(lateCheckoutDate).toDate() : undefined}
          minDate={convert(earliestDayForLateCheckout).toDate()}
          maxDate={convert(lastDayForLateCheckout).toDate()}
          onDateChange={(date: Date) => {
            onUpdate('lateCheckoutDate', localDateFromJsDate(date).toString());
          }}
        />
      </Box>

      <Text mt="4" size="s1">
        Rent {offer.shouldSeeAgentExperience ? 'the' : 'your'} home back for up to{' '}
        {LCO_MAX_DAYS_AFTER_COE} days with late checkout. Be sure to schedule by{' '}
        {offer.scheduleLateCheckoutDeadline.toLocalDate().format(FULL_DATE_AND_DAY_OF_WEEK_FORMAT)}.
      </Text>
      <Box as="hr" my="6" w="100%" opacity={0.16} />

      <LateCheckoutCosts
        offer={offer}
        lateCheckoutTask={lateCheckoutTask}
        lateCheckoutDateOverride={lateCheckoutDate ? LocalDate.from(lateCheckoutDate) : undefined}
      />
      <Box as="hr" my="6" w="100%" opacity={0.16} />

      <Flex flexDirection={['column', 'column', 'row']} gridGap="4">
        <Button
          aria-label="Save late checkout request"
          analyticsName="cake-save-late-checkout"
          data-testid="save-late-checkout"
          disabled={
            !isEdited || requestingLateCheckout || canceling || !lateCheckoutDate || !occupancy
          }
          loading={requestingLateCheckout}
          onClick={onSubmit}
          variant="primary"
        >
          Save
        </Button>
        {canCancelLateCheckout && (
          <Button
            aria-label="Cancel late checkout request"
            analyticsName="cake-cancel-late-checkout"
            data-testid="cancel-late-checkout"
            disabled={requestingLateCheckout || canceling}
            onClick={onCanceled}
            variant="secondary"
          >
            Cancel
          </Button>
        )}
        {cancelLateCheckout && (
          <Button
            aria-label={null}
            analyticsName="cake-remove-late-checkout"
            data-testid="remove-late-checkout"
            disabled={requestingLateCheckout || canceling}
            loading={canceling}
            onClick={cancelLateCheckout}
            variant="secondary"
          >
            Remove late checkout
          </Button>
        )}
      </Flex>
      <Text mt="3" size="s00">
        No payment today, cost is taken out of{' '}
        {offer.shouldSeeAgentExperience ? 'your client’s' : 'your'} net proceeds.
      </Text>
    </>
  );
};
